import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Images, GitCommitHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { AppShell } from "@/components/dash/AppShell";
import { AIAnalysisCard, QualityBadge, DetailSection } from "@/components/AIAnalysisCard";
import { PhotoGallery } from "@/components/dash/PhotoGallery";
import { ContactActions } from "@/components/dash/ContactActions";
import { LeadAnswers } from "@/components/dash/LeadAnswers";
import { StatusTimeline } from "@/components/dash/StatusTimeline";
import { VisitChecklist } from "@/components/dash/VisitChecklist";
import { StatusPill } from "@/components/dash/LeadCard";
import { timeAgo } from "@/lib/constants";
import { LeadPreview } from "@/components/dash/LeadPreview";

const DetailSkeleton = () => (
  <div className="mt-4 space-y-4">
    <div className="h-8 w-1/2 animate-pulse rounded-lg bg-muted" />
    <div className="h-56 animate-pulse rounded-2xl bg-muted" />
    <div className="h-40 animate-pulse rounded-2xl bg-muted" />
  </div>
);

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => api.get(`/leads/${id}`).then(({ data }) => setLead(data)).catch(() => setNotFound(true)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (lead?.ai_status !== "pending" && lead?.preview_status !== "pending") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [lead?.ai_status, lead?.preview_status, load]);

  const changeStatus = async (status) => {
    try { await api.patch(`/leads/${id}/status`, { status }); setLead((l) => ({ ...l, status })); toast.success("Status updated"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const retry = async () => {
    try {
      const { data } = await api.post(`/leads/${id}/analyze`);
      setLead((l) => ({ ...l, ai_status: "pending", ai_retry_count: data.ai_retry_count }));
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <AppShell hideNav={!!lead}>
      <Link to="/dashboard" className="inline-flex h-10 items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="lead-back-link">
        <ArrowLeft className="h-4 w-4" /> All leads
      </Link>
      {notFound ? <p className="mt-6 text-muted-foreground" data-testid="lead-not-found">This request could not be found.</p>
        : !lead ? <DetailSkeleton />
        : (
          <div className="mt-2 space-y-4" data-testid="lead-detail">
            <header className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl" data-testid="lead-detail-name">{lead.contact.name}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{lead.service_type} · {lead.contact.city} · {timeAgo(lead.created_at)}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                {lead.ai_analysis && <QualityBadge quality={lead.ai_analysis.lead_quality} />}
                <StatusPill status={lead.status} testId="lead-detail-status" />
              </div>
            </header>
            <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
              <div className="space-y-4">
                <DetailSection icon={Images} title={`Photos (${lead.photos.length})`}><PhotoGallery photos={lead.photos} /></DetailSection>
                <LeadPreview lead={lead} />
                <AIAnalysisCard lead={lead} onRetry={retry} hideQuestions />
                {lead.ai_status === "done" && <VisitChecklist key={lead.id} lead={lead} />}
              </div>
              <div className="space-y-4">
                <DetailSection icon={GitCommitHorizontal} title="Status"><StatusTimeline value={lead.status} onChange={changeStatus} /></DetailSection>
                <LeadAnswers lead={lead} />
              </div>
            </div>
          </div>
        )}
      {lead && <ContactActions contact={lead.contact} serviceType={lead.service_type} />}
    </AppShell>
  );
}
